import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Button,
    Stack,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch,
    Avatar,
    MenuItem,
    Select,
    InputAdornment,
    Autocomplete,
    Checkbox,
    Input
} from '@mui/material';
import { Plus, Trash2, SquarePen, Upload, X, Search, LayoutGrid, } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getAllCenters, createCenter, updateCenter, deleteCenter, getCenterCategories, getCenterById, deleteCenterImage } from '../../Services/ContentHub/SearchCentersApi';
import { getAllFlags } from '../../Services/ContentHub/FlagsApi';
import DeleteModal from '../../Components/Modals/DeleteModal';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';
import { generateSlug, normalizeSlugInput, cleanSlugFinal } from '../../utils.js/slugUtils';
import GlobalPagination from '../../Components/Common/Globalpagination';

const safeParseArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const GetSearchCenters = () => {
    const navigate = useNavigate();
    const { showSnackbar } = useGlobalSnackbar();
    const [centers, setCenters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [categoriesList, setCategoriesList] = useState([]);

    // Modal states
    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentCenter, setCurrentCenter] = useState(null);
    const [countryCodes, setCountryCodes] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        countryCode: '+91',
        phone: '',
        location: '',
        website: '',
        email: '',
        description: '',
        categoryIds: [],
        tripAdvisorUrl: '',
        mapIframe: '',
        isActive: true,
        heading: '',
        metaTitle: '',
        metaDescription: '',
        metaKeywords: '',
        metaOutlet: [],
        canonicalUrl: '',
        isIndex: true,
        imageAltText: ''
    });
    const [newMetaOutletInput, setNewMetaOutletInput] = useState('');
    const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);
    const [images, setImages] = useState([]); // Array of { file?: File, preview: string, id?: number }
    const [ogImage, setOgImage] = useState(null);
    const [ogImagePreview, setOgImagePreview] = useState(null);
    const [ogImageError, setOgImageError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [modalError, setModalError] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});

    const handleAddMetaOutletItem = () => {
        const trimmed = newMetaOutletInput.trim();
        if (!trimmed) return;
        setFormData(prev => ({
            ...prev,
            metaOutlet: [...(prev.metaOutlet || []), trimmed]
        }));
        setNewMetaOutletInput('');
        if (fieldErrors.metaOutlet) {
            setFieldErrors(prev => {
                const updated = { ...prev };
                delete updated.metaOutlet;
                return updated;
            });
        }
    };

    const handleRemoveMetaOutletItem = (index) => {
        setFormData(prev => ({
            ...prev,
            metaOutlet: (prev.metaOutlet || []).filter((_, idx) => idx !== index)
        }));
    };

    // OG Image Handlers
    const handleOgImageChange = (e) => {
        const file = e.target.files && e.target.files[0];
        const inputElement = e.target;
        if (file) {
            if (!file.type.startsWith('image/')) {
                setOgImageError('Only image files are allowed.');
                setOgImage(null);
                setOgImagePreview(null);
                inputElement.value = "";
                return;
            }
            if (file.size > 2 * 1024 * 1024) {
                setOgImageError('Image size must be less than 2MB.');
                setOgImage(null);
                setOgImagePreview(null);
                inputElement.value = "";
                return;
            }
            setOgImage(file);
            setOgImageError('');
            const reader = new FileReader();
            reader.onloadend = () => {
                setOgImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveOgImage = () => {
        setOgImage(null);
        setOgImagePreview(null);
        setOgImageError('');
    };

    // Delete modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedCenterId, setSelectedCenterId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    // Pagination states
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    // Fetch country codes
    useEffect(() => {
        const fetchCountryCodes = async () => {
            try {
                const response = await getAllFlags();
                if (response && response.success && Array.isArray(response.data)) {
                    const activeFlags = response.data.filter(item => item.isActive !== false);
                    setCountryCodes(activeFlags.length > 0 ? activeFlags : response.data);
                }
            } catch (err) {
                console.error("Error fetching country codes:", err);
            }
        };
        fetchCountryCodes();
    }, []);

    const defaultCountryCode = React.useMemo(() => {
        const defaultFlag = countryCodes.find(item => item.isDefault);
        return defaultFlag?.countryCode || '+91';
    }, [countryCodes]);

    const displayCountryCodes = React.useMemo(() => {
        let list = [...countryCodes];
        const currentCode = formData.countryCode || defaultCountryCode;
        if (!list.some(item => item.countryCode === currentCode)) {
            list.unshift({ countryCode: currentCode, name: '' });
        }
        if (list.length === 0) {
            list = [
                { countryCode: '+91', name: 'India' },
                { countryCode: '+1', name: 'USA/Canada' },
                { countryCode: '+44', name: 'UK' },
            ];
        }
        return list;
    }, [countryCodes, formData.countryCode, defaultCountryCode]);

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await getCenterCategories();
                if (response && response.success) {
                    setCategoriesList(response.data || []);
                }
            } catch (err) {
                console.error("Error fetching categories:", err);
            }
        };
        fetchCategories();
    }, []);

    // Reset to page 1 when filters change
    useEffect(() => {
        setPage(1);
    }, [debouncedSearchTerm, statusFilter]);

    // Fetch centers
    const fetchCentersData = useCallback(async () => {
        try {
            setLoading(true);
            const params = {
                page: page
            };
            if (rowsPerPage !== 10) params.limit = rowsPerPage;
            const response = await getAllCenters(debouncedSearchTerm, statusFilter, '', params);
            if (response && response.success) {
                const count = response.totalRecords || response.totalItems || 0;
                setCenters(response.data || []);
                setTotalItems(count);
                setTotalPages(response.totalPages || Math.ceil(count / rowsPerPage) || 1);
            } else {
                setError("Failed to load centers.");
            }
        } catch (err) {
            setError("Failed to fetch centers. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, [debouncedSearchTerm, statusFilter, page, rowsPerPage]);

    useEffect(() => {
        fetchCentersData();
    }, [fetchCentersData]);

    const handleOpenModal = async (center = null) => {
        setOpenModal(true);
        setModalError(null);
        setFieldErrors({});

        if (center) {
            setIsEdit(true);
            setCurrentCenter(center);
            setModalLoading(true);

            try {
                // Fetch complete center data from API
                const response = await getCenterById(center.id);

                if (response && response.success) {
                    const centerData = response.data;

                    setFormData({
                        name: centerData.name || '',
                        slug: centerData.slug ? cleanSlugFinal(centerData.slug) : (centerData.name ? generateSlug(centerData.name) : ''),
                        countryCode: centerData.countryCode || defaultCountryCode,
                        phone: centerData.phone || '',
                        location: centerData.location || '',
                        website: centerData.website || '',
                        email: centerData.email || '',
                        description: centerData.description || '',
                        categoryIds: centerData.categories ? centerData.categories.map(c => c.id) : [],
                        tripAdvisorUrl: centerData.tripAdvisorUrl || '',
                        mapIframe: centerData.mapIframe || '',
                        isActive: centerData.isActive !== undefined ? centerData.isActive : true,
                        heading: centerData.heading || '',
                        metaTitle: centerData.metaTitle || '',
                        metaDescription: centerData.metaDescription || '',
                        metaKeywords: centerData.metaKeywords || '',
                        metaOutlet: safeParseArray(centerData.metaOutlet),
                        canonicalUrl: centerData.canonicalUrl || '',
                        isIndex: centerData.isIndex !== undefined ? (centerData.isIndex === "true" || centerData.isIndex === true) : true,
                        imageAltText: centerData.imageAltText || ''
                    });
                    setIsSlugManuallyEdited(false);
                    setImages(centerData.images && centerData.images.length > 0 ? centerData.images.map(img => ({ preview: img.image, id: img.id })) : []);
                    setOgImagePreview(centerData.ogImage || null);
                    setOgImage(null);
                    setOgImageError('');
                } else {
                    // Fallback to the data from the list if API call fails
                    setFormData({
                        name: center.name || '',
                        slug: center.slug ? cleanSlugFinal(center.slug) : (center.name ? generateSlug(center.name) : ''),
                        countryCode: center.countryCode || defaultCountryCode,
                        phone: center.phone || '',
                        location: center.location || '',
                        website: center.website || '',
                        email: center.email || '',
                        description: center.description || '',
                        categoryIds: center.categories ? center.categories.map(c => c.id) : [],
                        tripAdvisorUrl: center.tripAdvisorUrl || '',
                        mapIframe: center.mapIframe || '',
                        isActive: center.isActive !== undefined ? center.isActive : true,
                        heading: center.heading || '',
                        metaTitle: center.metaTitle || '',
                        metaDescription: center.metaDescription || '',
                        metaKeywords: center.metaKeywords || '',
                        metaOutlet: safeParseArray(center.metaOutlet),
                        canonicalUrl: center.canonicalUrl || '',
                        isIndex: center.isIndex !== undefined ? (center.isIndex === "true" || center.isIndex === true) : true,
                        imageAltText: center.imageAltText || ''
                    });
                    setIsSlugManuallyEdited(false);
                    setImages(center.images && center.images.length > 0 ? center.images.map(img => ({ preview: img.image, id: img.id })) : (center.logo ? [{ preview: center.logo }] : []));
                    setOgImagePreview(center.ogImage || null);
                    setOgImage(null);
                    setOgImageError('');
                }
            } catch (error) {
                console.error("Error fetching center details:", error);
                // Fallback to the data from the list if API call fails
                setFormData({
                    name: center.name || '',
                    slug: center.slug ? cleanSlugFinal(center.slug) : (center.name ? generateSlug(center.name) : ''),
                    countryCode: center.countryCode || defaultCountryCode,
                    phone: center.phone || '',
                    location: center.location || '',
                    website: center.website || '',
                    email: center.email || '',
                    description: center.description || '',
                    categoryIds: center.categories ? center.categories.map(c => c.id) : [],
                    tripAdvisorUrl: center.tripAdvisorUrl || '',
                    mapIframe: center.mapIframe || '',
                    isActive: center.isActive !== undefined ? center.isActive : true,
                    heading: center.heading || '',
                    metaTitle: center.metaTitle || '',
                    metaDescription: center.metaDescription || '',
                    metaKeywords: center.metaKeywords || '',
                    metaOutlet: safeParseArray(center.metaOutlet),
                    canonicalUrl: center.canonicalUrl || '',
                    isIndex: center.isIndex !== undefined ? (center.isIndex === "true" || center.isIndex === true) : true,
                    imageAltText: center.imageAltText || ''
                });
                setIsSlugManuallyEdited(false);
                setImages(center.images && center.images.length > 0 ? center.images.map(img => ({ preview: img.image, id: img.id })) : (center.logo ? [{ preview: center.logo }] : []));
                setOgImagePreview(center.ogImage || null);
                setOgImage(null);
                setOgImageError('');
            } finally {
                setModalLoading(false);
            }
        } else {
            setIsEdit(false);
            setCurrentCenter(null);
            setFormData({
                name: '',
                slug: '',
                countryCode: defaultCountryCode,
                phone: '',
                location: '',
                website: '',
                email: '',
                description: '',
                categoryIds: [],
                tripAdvisorUrl: '',
                mapIframe: '',
                isActive: true,
                heading: '',
                metaTitle: '',
                metaDescription: '',
                metaKeywords: '',
                metaOutlet: [],
                canonicalUrl: '',
                isIndex: true,
                imageAltText: ''
            });
            setNewMetaOutletInput('');
            setIsSlugManuallyEdited(false);
            setImages([]);
            setOgImage(null);
            setOgImagePreview(null);
            setOgImageError('');
        }
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setIsEdit(false);
        setCurrentCenter(null);
        setFormData({
            name: '',
            slug: '',
            countryCode: defaultCountryCode,
            phone: '',
            location: '',
            website: '',
            email: '',
            description: '',
            categoryIds: [],
            tripAdvisorUrl: '',
            mapIframe: '',
            isActive: true,
            heading: '',
            metaTitle: '',
            metaDescription: '',
            metaKeywords: '',
            metaOutlet: [],
            canonicalUrl: '',
            isIndex: true,
            imageAltText: ''
        });
        setNewMetaOutletInput('');
        setIsSlugManuallyEdited(false);
        setImages([]);
        setOgImage(null);
        setOgImagePreview(null);
        setOgImageError('');
        setError(null);
        setModalLoading(false);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name === 'name') {
            const sanitizedValue = value.replace(/[^a-zA-Z\s]/g, '');
            setFormData(prev => ({
                ...prev,
                name: sanitizedValue,
                ...(!isSlugManuallyEdited ? { slug: generateSlug(sanitizedValue) } : {})
            }));
            if (fieldErrors.name) {
                setFieldErrors(prev => {
                    const updated = { ...prev };
                    delete updated.name;
                    return updated;
                });
            }
            if (!isSlugManuallyEdited && fieldErrors.slug) {
                setFieldErrors(prev => {
                    const updated = { ...prev };
                    delete updated.slug;
                    return updated;
                });
            }
            return;
        }

        if (name === 'location') {
            if (value && value.trim() && !/^[a-zA-Z0-9]/.test(value.trim())) {
                setFieldErrors(prev => ({
                    ...prev,
                    location: 'Location cannot start with special characters'
                }));
            } else if (fieldErrors.location) {
                setFieldErrors(prev => {
                    const updated = { ...prev };
                    delete updated.location;
                    return updated;
                });
            }
        }

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (fieldErrors[name]) {
            setFieldErrors(prev => {
                const updated = { ...prev };
                delete updated[name];
                return updated;
            });
        }
    };

    const handleSlugChange = (e) => {
        const rawVal = e.target.value;
        const normalizedVal = normalizeSlugInput(rawVal);
        setFormData(prev => ({
            ...prev,
            slug: normalizedVal
        }));
        setIsSlugManuallyEdited(true);
        if (fieldErrors.slug) {
            setFieldErrors(prev => {
                const updated = { ...prev };
                delete updated.slug;
                return updated;
            });
        }
    };

    const handleSlugBlur = () => {
        if (!formData.slug) {
            setFormData(prev => ({ ...prev, slug: '' }));
            return;
        }
        const cleaned = cleanSlugFinal(formData.slug);
        if (cleaned !== formData.slug) {
            setFormData(prev => ({ ...prev, slug: cleaned }));
        }
    };

    const handleLogoChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const newFiles = Array.from(e.target.files);

            const invalidFiles = newFiles.filter(file => !file.type.startsWith('image/'));
            if (invalidFiles.length > 0) {
                setFieldErrors(prev => ({
                    ...prev,
                    images: 'Only image files (PNG, JPG, WEBP, SVG, GIF) are allowed'
                }));
                showSnackbar("Only image files are allowed", "error");
                e.target.value = '';
                return;
            }

            const newImageObjects = newFiles.map(file => ({
                file: file,
                preview: URL.createObjectURL(file)
            }));

            setImages(prev => {
                const updated = [...prev, ...newImageObjects];
                return updated;
            });

            if (fieldErrors.images) {
                setFieldErrors(prev => {
                    const updated = { ...prev };
                    delete updated.images;
                    return updated;
                });
            }
        }
    };

    const handleRemoveLogo = async (index) => {
        const imageToRemove = images[index];

        // If it's an existing image (has an ID), delete it via API
        if (imageToRemove.id) {
            try {
                const response = await deleteCenterImage(imageToRemove.id);
                if (response && response.success) {
                    setImages(prev => {
                        const updated = prev.filter((_, i) => i !== index);
                        return updated;
                    });
                    if (fieldErrors.images) {
                        setFieldErrors(prev => {
                            const updated = { ...prev };
                            delete updated.images;
                            return updated;
                        });
                    }
                    // Refresh the table data to reflect the change
                    fetchCentersData();
                } else {
                    showSnackbar(response?.message || "Failed to delete image", "error");
                }
            } catch (error) {
                console.error("Error deleting image:", error);
                showSnackbar("An error occurred while deleting the image", "error");
            }
        } else {
            // If it's a new (blob) image, just remove it from state
            if (imageToRemove.preview.startsWith('blob:')) {
                URL.revokeObjectURL(imageToRemove.preview);
            }
            setImages(prev => {
                const updated = prev.filter((_, i) => i !== index);
                return updated;
            });
            if (fieldErrors.images) {
                setFieldErrors(prev => {
                    const updated = { ...prev };
                    delete updated.images;
                    return updated;
                });
            }
        }
    };

    const handleSubmit = async () => {
        setFieldErrors({});
        setModalError(null);

        const errors = {};
        if (!formData.name || !formData.name.trim()) {
            errors.name = "Center Name is required";
        } else if (!/^[a-zA-Z\s]+$/.test(formData.name.trim())) {
            errors.name = "Center Name cannot contain numbers or special characters";
        }

        const finalSlug = cleanSlugFinal(formData.slug);
        if (!finalSlug) {
            errors.slug = "Slug is required";
        } else if (centers.some(c => c.id !== currentCenter?.id && cleanSlugFinal(c.slug) === finalSlug)) {
            errors.slug = "This slug is already in use by another center";
        }

        if (formData.location && formData.location.trim() && !/^[a-zA-Z0-9]/.test(formData.location.trim())) {
            errors.location = "Location cannot start with special characters";
        }

        if (formData.phone && formData.phone.length !== 10) {
            errors.phone = "Phone number must be exactly 10 digits";
        }

        const invalidNewFiles = images.filter(img => img.file && img.file instanceof File && !img.file.type.startsWith('image/'));
        if (invalidNewFiles.length > 0) {
            setFieldErrors(prev => ({
                ...prev,
                images: 'Only image files (PNG, JPG, WEBP, SVG, GIF) are allowed'
            }));
            showSnackbar("Only image files are allowed", "error");
            return;
        }

        if (Object.keys(errors).length > 0) {
            setModalError("Please correct the validation errors");
            setFieldErrors(errors);
            return;
        }

        setSubmitting(true);
        setError(null);

        const data = new FormData();
        data.append('name', formData.name);
        data.append('slug', finalSlug);
        data.append('countryCode', formData.countryCode || defaultCountryCode);
        data.append('phone', formData.phone);
        data.append('location', formData.location);
        data.append('website', formData.website);
        data.append('email', formData.email);
        data.append('description', formData.description);

        // Send categoryId as an array JSON string
        if (formData.categoryIds && formData.categoryIds.length > 0) {
            data.append('categoryId', JSON.stringify(formData.categoryIds));
        }

        data.append('tripAdvisorUrl', formData.tripAdvisorUrl);
        data.append('mapIframe', formData.mapIframe);
        data.append('status', formData.isActive);

        data.append('heading', formData.heading ? formData.heading.trim() : '');
        data.append('metaTitle', formData.metaTitle ? formData.metaTitle.trim() : '');
        data.append('metaDescription', formData.metaDescription ? formData.metaDescription.trim() : '');
        data.append('metaKeywords', formData.metaKeywords ? formData.metaKeywords.trim() : '');
        data.append('metaOutlet', JSON.stringify(formData.metaOutlet || []));
        data.append('canonicalUrl', formData.canonicalUrl ? formData.canonicalUrl.trim() : '');
        data.append('isIndex', formData.isIndex ? "true" : "false");
        data.append('imageAltText', formData.imageAltText ? formData.imageAltText.trim() : '');

        // Filter for new files only to upload
        const newFiles = images.filter(img => img.file).map(img => img.file);

        if (newFiles.length > 0) {
            newFiles.forEach((file) => {
                data.append('centerImages', file);
            });
        }

        // Add OG Image if present
        if (ogImage) {
            data.append('ogImage', ogImage);
        }

        try {
            if (isEdit) {
                await updateCenter(currentCenter.id, data);
                showSnackbar("Center updated successfully!", "success");
            } else {
                await createCenter(data);
                showSnackbar("Center created successfully!", "success");
            }

            handleCloseModal();
            fetchCentersData();
        } catch (err) {
            const responseData = err.response?.data;
            const errMsg = responseData?.message || err.message || `Failed to ${isEdit ? 'update' : 'create'} center`;

            // Helper to match general or unknown error messages to specific form fields
            const mapMessageToField = (messageStr, targetObj) => {
                const msg = messageStr.toLowerCase();
                if (msg.includes('countrycode') || msg.includes('country code')) {
                    targetObj['countryCode'] = messageStr;
                } else if (msg.includes('phone')) {
                    targetObj['phone'] = messageStr;
                } else if (msg.includes('email')) {
                    targetObj['email'] = messageStr;
                } else if (msg.includes('name')) {
                    targetObj['name'] = messageStr;
                } else if (msg.includes('slug')) {
                    targetObj['slug'] = messageStr;
                } else if (msg.includes('website')) {
                    targetObj['website'] = messageStr;
                } else if (msg.includes('location')) {
                    targetObj['location'] = messageStr;
                } else if (msg.includes('description') && !msg.includes('metadescription') && !msg.includes('meta description')) {
                    targetObj['description'] = messageStr;
                } else if (msg.includes('category') || msg.includes('categories')) {
                    targetObj['categoryIds'] = messageStr;
                } else if (msg.includes('tripadvisor')) {
                    targetObj['tripAdvisorUrl'] = messageStr;
                } else if (msg.includes('map') || msg.includes('iframe')) {
                    targetObj['mapIframe'] = messageStr;
                } else if (msg.includes('image') || msg.includes('gallery')) {
                    targetObj['images'] = messageStr;
                } else if (msg.includes('heading')) {
                    targetObj['heading'] = messageStr;
                } else if (msg.includes('metatitle') || msg.includes('meta title')) {
                    targetObj['metaTitle'] = messageStr;
                } else if (msg.includes('metadescription') || msg.includes('meta description')) {
                    targetObj['metaDescription'] = messageStr;
                } else if (msg.includes('metakeywords') || msg.includes('meta keywords')) {
                    targetObj['metaKeywords'] = messageStr;
                } else if (msg.includes('metaoutlet') || msg.includes('meta outlet')) {
                    targetObj['metaOutlet'] = messageStr;
                } else if (msg.includes('canonicalurl') || msg.includes('canonical url')) {
                    targetObj['canonicalUrl'] = messageStr;
                } else if (msg.includes('isindex') || msg.includes('is index')) {
                    targetObj['isIndex'] = messageStr;
                }
            };

            // If the error response contains validation details, parse them into fieldErrors
            if (responseData?.details && Array.isArray(responseData.details)) {
                const errorsObj = {};
                responseData.details.forEach(item => {
                    if (item.field && item.message) {
                        const fieldName = item.field.toLowerCase();
                        if (fieldName === 'general') {
                            mapMessageToField(item.message, errorsObj);
                        } else if (fieldName === 'countrycode' || fieldName === 'country_code') {
                            errorsObj['countryCode'] = item.message;
                        } else if (fieldName === 'categoryid' || fieldName === 'categoryids') {
                            errorsObj['categoryIds'] = item.message;
                        } else if (fieldName === 'centerimages' || fieldName === 'images' || fieldName === 'gallery') {
                            errorsObj['images'] = item.message;
                        } else {
                            errorsObj[item.field] = item.message;
                        }
                    }
                });
                setFieldErrors(errorsObj);
                if (Object.keys(errorsObj).length === 0) {
                    setModalError(responseData.message || "Validation failed");
                }
            } else if (responseData?.errors && Array.isArray(responseData.errors) && responseData.errors.length > 0) {
                const errorsObj = {};
                responseData.errors.forEach(errStr => {
                    mapMessageToField(errStr, errorsObj);
                });
                setFieldErrors(errorsObj);
                if (Object.keys(errorsObj).length === 0) {
                    setModalError(responseData.errors.join(', '));
                }
            } else {
                // Check if the error message is about a specific field if no details or errors arrays exist
                const errorsObj = {};
                mapMessageToField(errMsg, errorsObj);
                if (Object.keys(errorsObj).length > 0) {
                    setFieldErrors(errorsObj);
                } else {
                    setModalError(errMsg);
                }
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedCenterId(id);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (selectedCenterId) {
            try {
                setIsDeleting(true);
                setDeleteError(null);
                const response = await deleteCenter(selectedCenterId);
                if (response && response.success) {
                    showSnackbar("Center deleted successfully!", "success");
                    setDeleteModalOpen(false);
                    setSelectedCenterId(null);
                    fetchCentersData();
                } else {
                    const errMsg = response?.message || "Failed to delete center";
                    setDeleteError(errMsg);
                    showSnackbar(errMsg, "error");
                }
            } catch (err) {
                console.error("Delete Error:", err);
                const responseData = err.response?.data;
                const errorMessage = responseData?.errors?.[0] ||
                    responseData?.details?.[0]?.message ||
                    responseData?.message ||
                    err.message ||
                    "Failed to delete center";
                setDeleteError(errorMessage);
                showSnackbar(errorMessage, "error");
            } finally {
                setIsDeleting(false);
            }
        }
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                {/* Header Section */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Typography sx={{ color: '#222', fontSize: { xs: '1.5rem', md: '1.8rem' }, fontWeight: 500 }}>
                        Search Centers
                    </Typography>
                    <Stack direction="row" spacing={1.5}>
                        <PermissionWrapper permission="view" mode="disable">
                            <Button onClick={() => navigate('/members/centers/categories')}
                                disableElevation disableRipple disableFocusRipple disableTouchRipple
                                variant="outlined" startIcon={<LayoutGrid size={18} />}
                                sx={{
                                    borderColor: '#FFBD65',
                                    color: '#FFBD65',
                                    borderRadius: '6px',
                                    height: '40px',
                                    textTransform: 'none',
                                    fontSize: '0.85rem',
                                    '&:hover': { borderColor: '#FFBD65', bgcolor: 'rgba(255, 189, 101, 0.08)' }
                                }}
                            >
                                Search Categories
                            </Button>
                        </PermissionWrapper>
                        <PermissionWrapper permission="create" mode="disable">
                            <Button onClick={() => handleOpenModal()}
                                disableElevation disableRipple disableFocusRipple disableTouchRipple
                                variant="contained" startIcon={<Plus size={18} />}
                                sx={{
                                    bgcolor: '#FFBD65',
                                    color: '#fff',
                                    borderRadius: '6px',
                                    height: '40px',
                                    textTransform: 'none',
                                    fontSize: '0.85rem',
                                }}
                            >
                                Add Center
                            </Button>
                        </PermissionWrapper>
                    </Stack>
                </Box>

                {/* Search and Filter Section */}
                <Box sx={{ mb: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <TextField
                        size="small" placeholder="Search by name, location..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{
                            bgcolor: '#fff', width: { xs: "100%", md: "30%" },
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '6px',
                                height: '40px',
                                fontSize: '0.85rem',
                                '& fieldset': { borderColor: '#E0E0E0' },
                                '&:hover fieldset': { borderColor: '#BDBDBD' },
                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ mr: 0.5 }}>
                                    <Search size={16} color="#999" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Select
                        size="small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                        displayEmpty
                        sx={{
                            minWidth: 130,
                            bgcolor: '#fff',
                            borderRadius: '6px',
                            height: '40px',
                            fontSize: '0.85rem',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BDBDBD' },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FFBD65', borderWidth: '1px' }
                        }}
                    >
                        <MenuItem value="" sx={{ fontSize: '0.85rem' }}>All Status</MenuItem>
                        <MenuItem value="true" sx={{ fontSize: '0.85rem' }}>Active</MenuItem>
                        <MenuItem value="false" sx={{ fontSize: '0.85rem' }}>Inactive</MenuItem>
                    </Select>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

                <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ minWidth: 1350 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center', width: '60px' }}>S.No</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '60px' }}>Image</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Slug</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Category</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Location</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Phone</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Email</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Created At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Updated At</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={14} align="center" sx={{ py: 8 }}>
                                            <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                        </TableCell>
                                    </TableRow>
                                ) : centers.length > 0 ? (
                                    centers.map((center, idx) => (
                                        <TableRow key={center.id} sx={{
                                            transition: 'background-color 0.2s',
                                            '& td': { borderBottom: '1px solid #F0F0F0' },
                                            '&:last-child td': { borderBottom: 0 }
                                        }}>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>{(page - 1) * rowsPerPage + idx + 1}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                <Avatar src={center.images[0]?.image} variant="rounded" sx={{ width: 32, height: 32, borderRadius: '4px' }} />
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', fontWeight: 550, color: '#333' }}>{center.name}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={center.slug}>
                                                {center.slug || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#555' }}>
                                                {(center.categories && center.categories.length > 0)
                                                    ? center.categories.map(c => c.name).join(', ')
                                                    : '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#555' }}>{center.location}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#555' }}>
                                                {center.phone ? `${center.countryCode ? `${center.countryCode} ` : ''}${center.phone}` : '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#555' }}>{center.email}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                <Box sx={{
                                                    display: 'inline-block',
                                                    px: 1.5, py: 0.3,
                                                    borderRadius: '4px',
                                                    bgcolor: center.isActive ? '#E8F5E9' : '#FFEBEE',
                                                    color: center.isActive ? '#2E7D32' : '#D32F2F',
                                                    fontSize: '0.7rem', fontWeight: 600
                                                }}>
                                                    {center.isActive ? 'Active' : 'Inactive'}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {center.cuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {center.uuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(center.createdAt)}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(center.updatedAt)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5 }}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <PermissionWrapper permission="edit" mode="disable">
                                                        <Tooltip title="Edit">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                                size="small" sx={{ color: '#FFBD65' }} onClick={() => handleOpenModal(center)}>
                                                                <SquarePen size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                    <PermissionWrapper permission="delete" mode="disable">
                                                        <Tooltip title="Delete">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                                size="small" sx={{ color: '#FF4D49' }} onClick={() => handleDeleteClick(center.id)}>
                                                                <Trash2 size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={14} align="center" sx={{ py: 4, color: '#AAA', fontSize: '0.8rem' }}>
                                            No centers found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    <GlobalPagination
                        page={page}
                        pageSize={rowsPerPage}
                        totalRecords={totalItems}
                        totalPages={totalPages}
                        onPageChange={(newPage) => setPage(newPage)}
                        onPageSizeChange={(newSize) => {
                            setRowsPerPage(newSize);
                            setPage(1);
                        }}
                        loading={loading}
                    />
                </Paper>

                {/* Add/Edit Modal */}
                <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '6px' } }}>
                    <DialogTitle sx={{ fontWeight: 600, color: '#222', fontSize: '1.1rem', pb: 1 }}>
                        {isEdit ? 'Edit Center' : 'Add New Center'}
                    </DialogTitle>
                    <DialogContent>
                        {modalLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
                                <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                            </Box>
                        ) : (
                            <Stack spacing={2} sx={{ mt: 2 }}>
                                {modalError && (
                                    <Alert severity="error" onClose={() => setModalError(null)} sx={{ py: 0.5, fontSize: '0.85rem' }}>
                                        {modalError}
                                    </Alert>
                                )}
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField size='small'
                                        fullWidth
                                        label="Center Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        error={Boolean(fieldErrors.name)}
                                        helperText={fieldErrors.name}
                                        sx={{
                                            flex: 1,
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.85rem',
                                                '& fieldset': { borderColor: '#E0E0E0' },
                                                '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                            }
                                        }}
                                    />
                                    <Box sx={{ flex: 1, display: 'flex', gap: 1, minWidth: 0 }}>
                                        <Select
                                            size="small"
                                            id="center-country-code-select"
                                            name="countryCode"
                                            value={formData.countryCode || defaultCountryCode}
                                            onChange={handleInputChange}
                                            renderValue={(selected) => {
                                                const item = displayCountryCodes.find(c => c.countryCode === selected);
                                                return (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                                        {item?.symbol && (
                                                            <Avatar
                                                                src={item.symbol}
                                                                variant="rounded"
                                                                sx={{ width: 18, height: 13, borderRadius: '2px' }}
                                                                imgProps={{ style: { objectFit: 'cover' } }}
                                                            />
                                                        )}
                                                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, lineHeight: 1 }}>
                                                            {selected}
                                                        </Typography>
                                                    </Box>
                                                );
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        width: 220,
                                                        maxHeight: 280,
                                                        borderRadius: '6px',
                                                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                                                    }
                                                }
                                            }}
                                            sx={{
                                                width: '95px',
                                                minWidth: '95px',
                                                flexShrink: 0,
                                                borderRadius: '6px',
                                                fontSize: '0.85rem',
                                                '& .MuiSelect-select': {
                                                    py: '8.5px',
                                                    pr: '26px !important',
                                                    pl: 1,
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                },
                                                '& fieldset': { borderColor: '#E0E0E0' },
                                                '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                            }}
                                        >
                                            {displayCountryCodes.map((item) => (
                                                <MenuItem key={item.id || item.countryCode} value={item.countryCode}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                                        {item.symbol && (
                                                            <Avatar
                                                                src={item.symbol}
                                                                variant="rounded"
                                                                sx={{ width: 20, height: 14, borderRadius: '2px' }}
                                                                imgProps={{ style: { objectFit: 'cover' } }}
                                                            />
                                                        )}
                                                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                                                            {item.countryCode}
                                                        </Typography>
                                                        {item.name && (
                                                            <Typography sx={{ fontSize: '0.75rem', color: '#888', ml: 'auto', pl: 1 }}>
                                                                {item.name}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>

                                        <TextField size='small'
                                            fullWidth
                                            label="Phone"
                                            name="phone"
                                            required
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            error={Boolean(fieldErrors.phone)}
                                            helperText={fieldErrors.phone}
                                            // placeholder="10 digits"
                                            sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '6px',
                                                    fontSize: '0.85rem',
                                                    '& fieldset': { borderColor: '#E0E0E0' },
                                                    '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                                }
                                            }}
                                        />
                                    </Box>
                                </Box>

                                <TextField size='small'
                                    fullWidth
                                    label="Slug"
                                    name="slug"
                                    value={formData.slug}
                                    onChange={handleSlugChange}
                                    onBlur={handleSlugBlur}
                                    placeholder="e.g. hyderabad-wellness-center"
                                    required
                                    error={Boolean(fieldErrors.slug)}
                                    helperText={fieldErrors.slug || "Automatically generated from name."}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            fontFamily: 'monospace',
                                            '& fieldset': { borderColor: '#E0E0E0' },
                                            '&:hover fieldset': { borderColor: '#BDBDBD' },
                                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                        }
                                    }}
                                />

                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField size='small'
                                        fullWidth
                                        label="Location"
                                        name="location"
                                        value={formData.location}
                                        onChange={handleInputChange}
                                        error={Boolean(fieldErrors.location)}
                                        helperText={fieldErrors.location}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.85rem',
                                                '& fieldset': { borderColor: '#E0E0E0' },
                                                '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                            }
                                        }}
                                    />
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        fullWidth
                                        options={categoriesList}
                                        disableCloseOnSelect
                                        getOptionLabel={(option) => option.name}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        value={categoriesList.filter(cat => formData.categoryIds.includes(cat.id))}
                                        onChange={(event, newValue) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                categoryIds: newValue.map(item => item.id)
                                            }));
                                            if (fieldErrors.categoryIds || fieldErrors.categoryId) {
                                                setFieldErrors(prev => {
                                                    const updated = { ...prev };
                                                    delete updated.categoryIds;
                                                    delete updated.categoryId;
                                                    return updated;
                                                });
                                            }
                                        }}
                                        renderOption={(props, option, { selected }) => (
                                            <li {...props} style={{ fontSize: '0.85rem' }}>
                                                <Checkbox
                                                    size="small"
                                                    style={{ marginRight: 8 }}
                                                    checked={selected}
                                                    sx={{
                                                        color: '#E0E0E0',
                                                        '&.Mui-checked': { color: '#FFBD65' }
                                                    }}
                                                />
                                                {option.name}
                                            </li>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Categories"
                                                placeholder="Search categories..."
                                                error={Boolean(fieldErrors.categoryIds || fieldErrors.categoryId)}
                                                helperText={fieldErrors.categoryIds || fieldErrors.categoryId}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '6px',
                                                        fontSize: '0.85rem',
                                                        '& fieldset': { borderColor: '#E0E0E0' },
                                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                                    }
                                                }}
                                            />
                                        )}
                                        sx={{
                                            '& .MuiAutocomplete-tag': {
                                                bgcolor: '#FFF8ED',
                                                borderRadius: '4px',
                                                fontSize: '0.8rem',
                                                '& .MuiChip-deleteIcon': {
                                                    color: '#FFBD65',
                                                }
                                            }
                                        }}
                                    />
                                </Box>

                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField size='small'
                                        fullWidth
                                        label="Website / Social links"
                                        name="website"
                                        required
                                        value={formData.website}
                                        onChange={handleInputChange}
                                        error={Boolean(fieldErrors.website)}
                                        helperText={fieldErrors.website}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.85rem',
                                                '& fieldset': { borderColor: '#E0E0E0' },
                                                '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                            }
                                        }}
                                    />
                                    <TextField size='small'
                                        fullWidth
                                        label="Email"
                                        name="email"
                                        required
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        error={Boolean(fieldErrors.email)}
                                        helperText={fieldErrors.email}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.85rem',
                                                '& fieldset': { borderColor: '#E0E0E0' },
                                                '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                            }
                                        }}
                                    />
                                </Box>

                                <TextField size='small'
                                    fullWidth
                                    label="TripAdvisor URL"
                                    name="tripAdvisorUrl"
                                    value={formData.tripAdvisorUrl}
                                    onChange={handleInputChange}
                                    error={Boolean(fieldErrors.tripAdvisorUrl)}
                                    helperText={fieldErrors.tripAdvisorUrl}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            '& fieldset': { borderColor: '#E0E0E0' },
                                            '&:hover fieldset': { borderColor: '#BDBDBD' },
                                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                        }
                                    }}
                                />

                                <TextField size='small'
                                    fullWidth
                                    label="Map Iframe (Google Maps Link)"
                                    name="mapIframe"
                                    value={formData.mapIframe}
                                    onChange={handleInputChange}
                                    multiline
                                    rows={2}
                                    error={Boolean(fieldErrors.mapIframe)}
                                    helperText={fieldErrors.mapIframe}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            '& fieldset': { borderColor: '#E0E0E0' },
                                            '&:hover fieldset': { borderColor: '#BDBDBD' },
                                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                        }
                                    }}
                                />

                                <TextField size='small'
                                    fullWidth
                                    label="Description"
                                    name="description"
                                    required
                                    multiline
                                    rows={3}
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    error={Boolean(fieldErrors.description)}
                                    helperText={fieldErrors.description}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            '& fieldset': { borderColor: '#E0E0E0' },
                                            '&:hover fieldset': { borderColor: '#BDBDBD' },
                                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                        }
                                    }}
                                />

                                <FormControlLabel
                                    control={<Switch checked={formData.isActive} onChange={handleInputChange} name="isActive"
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFBD65' },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FFBD65' }
                                        }}
                                    />}
                                    label={<Typography sx={{ fontSize: '0.85rem' }}>Active</Typography>}
                                />

                                <Box>
                                    <Typography sx={{ mb: 1, fontWeight: 550, color: fieldErrors.images ? '#d32f2f' : '#333', fontSize: '0.85rem' }}>Logo</Typography>
                                    <Box
                                        sx={{
                                            border: fieldErrors.images ? '2px dashed #d32f2f' : '2px dashed #E0E0E0',
                                            borderRadius: '6px', p: 2,
                                            textAlign: 'center',
                                            bgcolor: '#FAFAFA',
                                            cursor: 'pointer',
                                            position: 'relative',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                                            {images.map((img, index) => (
                                                <Box key={index} sx={{ position: 'relative', display: 'inline-block' }}>
                                                    <img src={img.preview} alt={`Preview ${index}`}
                                                        style={{ height: 60, width: 60, objectFit: 'cover', borderRadius: '4px' }}
                                                    />
                                                    <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                        size="small"
                                                        onClick={() => handleRemoveLogo(index)}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: -5,
                                                            right: -5,
                                                            bgcolor: '#FF4D49',
                                                            color: '#fff',
                                                            padding: '2px',
                                                            '&:hover': { bgcolor: '#e53935' }
                                                        }}
                                                    >
                                                        <X size={10} />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                        </Box>

                                        <label style={{ width: '100%', display: 'block', cursor: 'pointer', marginTop: images.length > 0 ? '12px' : '0' }}>
                                            <input type="file" accept="image/png, image/jpeg, image/jpg, image/webp, image/gif, image/svg+xml" multiple hidden onChange={handleLogoChange} />
                                            <Stack spacing={1} alignItems="center">
                                                <Box sx={{ p: 1, borderRadius: '50%', bgcolor: '#FFF8ED', color: '#FFBD65', display: 'inline-flex' }}>
                                                    <Upload size={18} />
                                                </Box>
                                                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#666' }}>Click to upload images</Typography>
                                                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#999' }}>PNG, JPG (max. 2MB)</Typography>
                                            </Stack>
                                        </label>
                                    </Box>
                                    {fieldErrors.images && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', pl: 1, fontSize: '0.75rem' }}>
                                            {fieldErrors.images}
                                        </Typography>
                                    )}
                                </Box>

                                {/* SEO Module */}
                                <Box sx={{
                                    border: '1px solid #E2E8F0',
                                    borderRadius: '6px',
                                    p: 2,
                                    bgcolor: '#FAFAFA'
                                }}>
                                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#1E293B', mb: 0.5 }}>
                                        SEO
                                    </Typography>
                                    <Typography sx={{ color: '#64748B', fontSize: '0.75rem', mb: 1.5 }}>
                                        Configure search engine optimization settings for this center.
                                    </Typography>
                                    <Stack spacing={1.5}>
                                        {/* Heading */}
                                        <TextField
                                            size="small"
                                            fullWidth
                                            label="Heading"
                                            name="heading"
                                            value={formData.heading}
                                            onChange={handleInputChange}
                                            placeholder="Enter heading..."
                                            error={Boolean(fieldErrors.heading)}
                                            helperText={fieldErrors.heading}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '6px',
                                                    fontSize: '0.85rem',
                                                    bgcolor: '#FFFFFF',
                                                    '& fieldset': { borderColor: '#E0E0E0' },
                                                    '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                                }
                                            }}
                                        />

                                        {/* Meta Title */}
                                        <Box>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                label="Meta Title"
                                                name="metaTitle"
                                                value={formData.metaTitle}
                                                onChange={handleInputChange}
                                                placeholder="Enter meta title..."
                                                inputProps={{ maxLength: 70 }}
                                                error={Boolean(fieldErrors.metaTitle)}
                                                helperText={fieldErrors.metaTitle}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '6px',
                                                        fontSize: '0.85rem',
                                                        bgcolor: '#FFFFFF',
                                                        '& fieldset': { borderColor: '#E0E0E0' },
                                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                                    }
                                                }}
                                            />
                                            <Typography variant="caption" sx={{ color: '#64748B', display: 'block', textAlign: 'right', mt: 0.5, fontSize: '0.72rem' }}>
                                                {formData.metaTitle?.length || 0} / 60 characters
                                            </Typography>
                                        </Box>

                                        {/* Meta Description */}
                                        <Box>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Meta Description"
                                                name="metaDescription"
                                                value={formData.metaDescription}
                                                onChange={handleInputChange}
                                                placeholder="Enter meta description..."
                                                inputProps={{ maxLength: 180 }}
                                                error={Boolean(fieldErrors.metaDescription)}
                                                helperText={fieldErrors.metaDescription}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '6px',
                                                        fontSize: '0.85rem',
                                                        bgcolor: '#FFFFFF',
                                                        '& fieldset': { borderColor: '#E0E0E0' },
                                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                                    }
                                                }}
                                            />
                                            <Typography variant="caption" sx={{ color: '#64748B', display: 'block', textAlign: 'right', mt: 0.5, fontSize: '0.72rem' }}>
                                                {formData.metaDescription?.length || 0} / 160 characters
                                            </Typography>
                                        </Box>

                                        {/* Meta Keywords */}
                                        <TextField
                                            size="small"
                                            fullWidth
                                            label="Meta Keywords"
                                            name="metaKeywords"
                                            value={formData.metaKeywords}
                                            onChange={handleInputChange}
                                            placeholder="Enter meta keywords (comma separated)..."
                                            error={Boolean(fieldErrors.metaKeywords)}
                                            helperText={fieldErrors.metaKeywords || "Comma separated keywords"}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '6px',
                                                    fontSize: '0.85rem',
                                                    bgcolor: '#FFFFFF',
                                                    '& fieldset': { borderColor: '#E0E0E0' },
                                                    '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                                }
                                            }}
                                        />

                                        {/* Meta Outlet */}
                                        <Box>
                                            <Typography sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.825rem', color: '#334155' }}>
                                                Meta Outlet (H2 Outlet)
                                            </Typography>
                                            <Typography sx={{ color: '#64748B', fontSize: '0.75rem', mb: 1.5 }}>
                                                Add meta outlet bullet points for this center.
                                            </Typography>

                                            <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
                                                <TextField
                                                    size="small"
                                                    placeholder="Add meta outlet item..."
                                                    value={newMetaOutletInput}
                                                    onChange={(e) => setNewMetaOutletInput(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            handleAddMetaOutletItem();
                                                        }
                                                    }}
                                                    fullWidth
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '6px',
                                                            fontSize: '0.85rem',
                                                            bgcolor: '#FFFFFF',
                                                            '& fieldset': { borderColor: '#E0E0E0' },
                                                            '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                                        }
                                                    }}
                                                />
                                                <Button
                                                    variant="contained"
                                                    startIcon={<Plus size={16} />}
                                                    onClick={handleAddMetaOutletItem}
                                                    sx={{
                                                        backgroundColor: '#1E293B',
                                                        color: '#FFFFFF',
                                                        textTransform: 'none',
                                                        boxShadow: 'none',
                                                        borderRadius: '6px',
                                                        fontWeight: 500,
                                                        px: 2.5,
                                                        fontSize: '0.8rem',
                                                        whiteSpace: 'nowrap',
                                                        '&:hover': { backgroundColor: '#334155', boxShadow: 'none' }
                                                    }}
                                                >
                                                    Add
                                                </Button>
                                            </Stack>

                                            {formData.metaOutlet && formData.metaOutlet.length > 0 && (
                                                <Stack spacing={1}>
                                                    {formData.metaOutlet.map((item, idx) => (
                                                        <Box
                                                            key={idx}
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                p: 1.25,
                                                                backgroundColor: '#FFFFFF',
                                                                border: '1px solid #E2E8F0',
                                                                borderRadius: '6px'
                                                            }}
                                                        >
                                                            <Typography sx={{ color: '#334155', fontSize: '0.8rem', fontWeight: 500 }}>
                                                                • {item}
                                                            </Typography>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleRemoveMetaOutletItem(idx)}
                                                                sx={{ color: '#94A3B8', p: 0.5, '&:hover': { color: '#EF4444', backgroundColor: '#FEE2E2' } }}
                                                            >
                                                                <Trash2 size={15} />
                                                            </IconButton>
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            )}
                                            {fieldErrors.metaOutlet && (
                                                <Typography variant="caption" sx={{ color: "#d32f2f", mt: 0.5, display: "block" }}>
                                                    {fieldErrors.metaOutlet}
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* Canonical URL */}
                                        <TextField
                                            size="small"
                                            fullWidth
                                            label="Canonical URL"
                                            name="canonicalUrl"
                                            value={formData.canonicalUrl}
                                            onChange={handleInputChange}
                                            placeholder="Enter canonical URL..."
                                            error={Boolean(fieldErrors.canonicalUrl)}
                                            helperText={fieldErrors.canonicalUrl}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '6px',
                                                    fontSize: '0.85rem',
                                                    bgcolor: '#FFFFFF',
                                                    '& fieldset': { borderColor: '#E0E0E0' },
                                                    '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                                }
                                            }}
                                        />

                                        {/* Is Index */}
                                        <Box>
                                            <Typography sx={{ mb: 0.75, fontSize: '0.825rem', fontWeight: 500, color: "#334155" }}>
                                                Is Index
                                            </Typography>
                                            <Box
                                                sx={{
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: '6px',
                                                    boxShadow: 'none',
                                                    p: 1.25,
                                                    bgcolor: '#FFFFFF'
                                                }}
                                            >
                                                <Stack direction="row" spacing={3} alignItems="center">
                                                    <Box
                                                        sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer' }}
                                                        onClick={() => {
                                                            setFormData(prev => ({ ...prev, isIndex: true }));
                                                            if (fieldErrors.isIndex) {
                                                                setFieldErrors(prev => {
                                                                    const updated = { ...prev };
                                                                    delete updated.isIndex;
                                                                    return updated;
                                                                });
                                                            }
                                                        }}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="isIndex"
                                                            checked={formData.isIndex === true}
                                                            onChange={() => {
                                                                setFormData(prev => ({ ...prev, isIndex: true }));
                                                                if (fieldErrors.isIndex) {
                                                                    setFieldErrors(prev => {
                                                                        const updated = { ...prev };
                                                                        delete updated.isIndex;
                                                                        return updated;
                                                                    });
                                                                }
                                                            }}
                                                            style={{ cursor: 'pointer', margin: 0 }}
                                                        />
                                                        <Typography sx={{ fontSize: '0.825rem', color: '#334155', cursor: 'pointer' }}>True (Indexable)</Typography>
                                                    </Box>
                                                    <Box
                                                        sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer' }}
                                                        onClick={() => {
                                                            setFormData(prev => ({ ...prev, isIndex: false }));
                                                            if (fieldErrors.isIndex) {
                                                                setFieldErrors(prev => {
                                                                    const updated = { ...prev };
                                                                    delete updated.isIndex;
                                                                    return updated;
                                                                });
                                                            }
                                                        }}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="isIndex"
                                                            checked={formData.isIndex === false}
                                                            onChange={() => {
                                                                setFormData(prev => ({ ...prev, isIndex: false }));
                                                                if (fieldErrors.isIndex) {
                                                                    setFieldErrors(prev => {
                                                                        const updated = { ...prev };
                                                                        delete updated.isIndex;
                                                                        return updated;
                                                                    });
                                                                }
                                                            }}
                                                            style={{ cursor: 'pointer', margin: 0 }}
                                                        />
                                                        <Typography sx={{ fontSize: '0.825rem', color: '#334155', cursor: 'pointer' }}>False (No Index)</Typography>
                                                    </Box>
                                                </Stack>
                                            </Box>
                                            {fieldErrors.isIndex && (
                                                <Typography variant="caption" sx={{ color: "#d32f2f", mt: 0.5, display: "block" }}>
                                                    {fieldErrors.isIndex}
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* Image Alt Text */}
                                        <Box>
                                            <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: "#334155" }}>
                                                Image Alt Text
                                            </Typography>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                placeholder="Enter image alt text"
                                                value={formData.imageAltText}
                                                onChange={(e) => setFormData(prev => ({ ...prev, imageAltText: e.target.value }))}
                                                error={Boolean(fieldErrors.imageAltText)}
                                                helperText={fieldErrors.imageAltText}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '6px',
                                                        fontSize: '0.85rem',
                                                        bgcolor: '#FFFFFF',
                                                        '& fieldset': { borderColor: '#E0E0E0' },
                                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                                    }
                                                }}
                                            />
                                        </Box>

                                        {/* OG Image */}
                                        <Box>
                                            <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: "#334155" }}>
                                                OG Image
                                            </Typography>
                                            {!ogImagePreview ? (
                                                <Box
                                                    component="label"
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        border: ogImageError ? "1px dashed #EF4444" : "1px dashed #CBD5E1",
                                                        borderRadius: "6px",
                                                        height: 140,
                                                        cursor: submitting ? "not-allowed" : "pointer",
                                                        bgcolor: "#F8FAFC",
                                                        transition: "all 0.2s ease-in-out",
                                                        "&:hover": {
                                                            borderColor: ogImageError ? "#EF4444" : "#6E8964",
                                                            bgcolor: "#F1F5F9"
                                                        }
                                                    }}
                                                >
                                                    <input
                                                        type="file"
                                                        accept="image/png, image/jpeg, image/jpg, image/webp, image/gif, image/svg+xml, image/*"
                                                        style={{ display: "none" }}
                                                        disabled={submitting}
                                                        onChange={handleOgImageChange}
                                                    />
                                                    <Upload size={22} color={ogImageError ? "#EF4444" : "#64748B"} style={{ marginBottom: 6 }} />
                                                    <Typography sx={{ color: ogImageError ? "#EF4444" : "#475569", fontSize: '0.8rem', fontWeight: 500 }}>
                                                        Click to upload OG image
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: ogImageError ? "#EF4444" : "#94A3B8", fontSize: '0.7rem' }}>
                                                        PNG, JPG or WebP (Max 2MB)
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Box sx={{ position: "relative", width: "100%", height: 140, bgcolor: "#F8FAFC", borderRadius: "6px", border: "1px solid #E2E8F0", display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                                    <Box
                                                        component="img"
                                                        src={ogImagePreview}
                                                        alt="OG Image Preview"
                                                        sx={{
                                                            maxWidth: "100%",
                                                            maxHeight: "100%",
                                                            objectFit: "contain",
                                                        }}
                                                    />
                                                    <IconButton
                                                        onClick={handleRemoveOgImage}
                                                        disabled={submitting}
                                                        sx={{
                                                            position: "absolute",
                                                            top: 6,
                                                            right: 6,
                                                            backgroundColor: "#FFFFFF",
                                                            border: "1px solid #E2E8F0",
                                                            color: "#EF4444",
                                                            p: 0.5,
                                                            boxShadow: 'none',
                                                            '&:hover': { bgcolor: '#FEE2E2' }
                                                        }}
                                                        size="small"
                                                    >
                                                        <X size={14} />
                                                    </IconButton>
                                                </Box>
                                            )}
                                            {ogImageError && (
                                                <Typography sx={{ color: '#EF4444', fontSize: '0.75rem', mt: 0.75, fontWeight: 400 }}>
                                                    {ogImageError}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Stack>
                                </Box>
                            </Stack>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, pt: 1 }}>
                        <Button disableElevation disableFocusRipple disableRipple disableTouchRipple onClick={handleCloseModal} sx={{ color: '#222', textTransform: 'none', fontSize: '0.85rem', px: 2.5 }}>
                            Cancel
                        </Button>
                        <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                            onClick={handleSubmit} disabled={submitting} variant="contained"
                            sx={{
                                bgcolor: '#FFBD65',
                                borderRadius: '6px',
                                textTransform: 'none',
                                px: 3,
                                color: '#fff',
                                fontSize: '0.85rem'
                            }}
                        >
                            {submitting ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Update' : 'Create')}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Modal */}
                <DeleteModal
                    open={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    onDelete={handleConfirmDelete}
                    title="Delete Center"
                    description="Are you sure you want to delete this center? This action cannot be undone."
                    error={deleteError}
                    loading={isDeleting}
                />
            </Box>
        </PermissionWrapper>
    );
};

export default GetSearchCenters;